import React from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import ApprovalRequests from '~/components/exposures/ApprovalRequests';
import ExposureMonetaryValueTextFieldFormik from '~/components/exposures/ExposureMonetaryValueTextFieldFormik';
import AnomalousPaymentBanner from '~/components/exposures/PaymentRequestContainer/MakePaymentRequestDialog/AnomalousPaymentBanner';
import useFetchPaymentPossibility from '~/components/exposures/PaymentRequestContainer/MakePaymentRequestDialog/useFetchPaymentRequestPossibility';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';
import toCents from '~/Utils/toCents';

import { capitalize } from '../../../Utils';
import CardDialog from '../../CardDialog';
import { LoadingSwitch, Text } from '../../core';
import { CurrencyFormatterContextProvider, useCurrencyFormatter } from '../../CurrencyFormatterContext';
import { usePaymentsLimits } from '../../hooks/usePaymentLimits';
import { useExposureSubReserves } from '../../hooks/useSubReserves';
import { MonetaryValueTextFieldFormik, TextFieldFormik } from '../../TextFieldFormik';
import ToggleButtonGroupFormik from '../../ToggleButtonGroupFormik';
import { isLimitExists } from '../ExposureUtils';

import AdditionalPaymentVerificationDialog from './MakePaymentRequestDialog/AdditionalPaymentVerification/AdditionalPaymentVerificationDialog';
import { getVatAmountToNetOut } from './netPaidReserveUtils';
import {
  getAnomalousPaymentBannerProps,
  getPaymentsAboveReserveIssuesAndSeverities,
  shouldDisablePayment,
} from './paymentContainerUtils';
import PaymentRequestDisplay from './PaymentRequestDisplay';
import usePreSubmitChecks from './usePreSubmitChecks';

import { useStyles } from '../../../assets/styles';
import styles from './PaymentRequestApprovalDialog.module.scss';

function PaymentRequestApprovalDialog(props) {
  const {
    paymentRequest,
    payableWithReserve,
    exposure,
    onDecidePaymentRequest,
    cardDialogProps,
    onCancel,
    claim,
    payableType,
    AdditionalDialogHeader,
  } = props;
  const { handlePreSubmitChecks, showPreSubmitCheckResultsDialog, setShowPreSubmitCheckResultsDialog } =
    usePreSubmitChecks({
      claim,
      exposure,
      payableType,
      currentPaymentRequestId: paymentRequest.id,
    });

  return (
    <Formik
      initialValues={{
        ...paymentRequest,
        note: '',
        decision: '',
        decision_amount: '',
        ...(paymentRequest?.request_amount_orig_currency
          ? { request_amount: paymentRequest?.request_amount_orig_currency }
          : {}),
      }}
      validationSchema={Yup.object().shape({
        note: Yup.string().required('Required'),
        decision: Yup.string().required('Required').oneOf(['approve', 'approve_different', 'decline']),
        decision_amount: Yup.number().when('decision', {
          is: 'approve_different',
          then: Yup.number().required('Required'),
        }),
      })}
      enableReinitialize
      onSubmit={async (values, formikProps) => {
        if (!showPreSubmitCheckResultsDialog.open && ['approve', 'approve_different'].includes(values.decision)) {
          const shouldStopSubmission = await handlePreSubmitChecks({ values, isApprovalFlow: true });
          if (shouldStopSubmission) {
            formikProps.setSubmitting(false);
            return;
          }
        }
        try {
          await onDecidePaymentRequest(values);
          formikProps.resetForm();
        } catch (error) {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, handleSubmit, setFieldValue, values }) => (
        <>
          <PaymentRequestApprovalDialogFormikInner
            paymentRequest={paymentRequest}
            payableWithReserve={payableWithReserve}
            exposure={exposure}
            cardDialogProps={cardDialogProps}
            onCancel={onCancel}
            claim={claim}
            payableType={payableType}
            AdditionalDialogHeader={AdditionalDialogHeader}
          />
          {showPreSubmitCheckResultsDialog.open && (
            <AdditionalPaymentVerificationDialog
              onClose={() => setShowPreSubmitCheckResultsDialog({ open: false })}
              isSubmitting={isSubmitting}
              values={values}
              disabled={isSubmitting}
              preSubmitChecks={showPreSubmitCheckResultsDialog.preSubmitChecks}
              onProceed={async (overrideDetails) => {
                setFieldValue('additional_verifications_extra', {
                  results: showPreSubmitCheckResultsDialog.preSubmitChecks,
                  override_details: overrideDetails,
                });
                await handleSubmit();
              }}
            />
          )}
        </>
      )}
    </Formik>
  );
}

PaymentRequestApprovalDialog.propTypes = {
  cardDialogProps: PropTypes.object.isRequired,
  paymentRequest: PropTypes.object.isRequired,
  payableWithReserve: PropTypes.object.isRequired,
  payableType: PropTypes.string.isRequired,
  onDecidePaymentRequest: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  claim: PropTypes.object.isRequired,
  exposure: PropTypes.object.isRequired,
  AdditionalDialogHeader: PropTypes.node,
};

function PaymentRequestApprovalDialogFormikInner({
  paymentRequest,
  payableWithReserve,
  exposure,
  cardDialogProps,
  onCancel,
  claim,
  payableType,
  AdditionalDialogHeader,
}) {
  const { classes } = useStyles();
  const {
    shouldDisablePaymentExceedingReserve,
    adjustReserveIncludesDeductible,
    isPaymentLimitExistsForPayableType,
    getPaymentLimitForPayableType,
    reduceReservesByNetPaid,
  } = usePaymentsConfiguration();
  const { currencyFormatter } = useCurrencyFormatter();
  const { userExposureLimit, isUserExposureLimitLoading } = usePaymentsLimits(
    claim,
    exposure,
    payableType,
    'payment',
    true
  );
  const { isSubReservesConfigEnabled, isMultipleSubReservePaymentsEnabled, subReservesConfigMap } =
    useExposureSubReserves(payableType, exposure);

  const { isSubmitting, handleSubmit, values, handleReset } = useFormikContext();

  const currentPaymentRequestAmount =
    (paymentRequest?.request_amount_orig_currency ? paymentRequest?.request_amount : values['request_amount']) || 0;
  // don't nullify amount on decline to continue show all warnings etc applicable if approved
  let amount = currentPaymentRequestAmount;
  if (values['decision'] === 'approve_different') {
    amount = values['decision_amount'] || 0;
  }

  const { isLoadingPaymentIssues, policyLimitBreaches, isPaymentIssuePreventingPayment } = useFetchPaymentPossibility(
    claim.id,
    exposure.isGeneralExpenses ? null : exposure.id,
    payableType,
    amount
  );

  const reserveAdjustmentAmount = toCents(
    adjustReserveIncludesDeductible && values['deductible_considered_amount']
      ? amount + values['deductible_considered_amount']
      : amount
  );

  const checkIsExceedsClosedExposurePaymentLimit = () => {
    return (
      (exposure.is_closed || claim.is_closed) &&
      isPaymentLimitExistsForPayableType(payableType) &&
      getPaymentLimitForPayableType(payableType) < amount
    );
  };

  // Approving takes the net amount out of the reserve when reserves are reduced by net paid, so the
  // above-reserve check has to net by the request's own VAT/GST, converted into the policy currency the
  // reserve amounts are held in — the mirror of `_get_vat_to_net_out` on the backend.
  const vatAmountToNetOut = getVatAmountToNetOut({
    shouldReduceReservesByNetPaid: reduceReservesByNetPaid,
    isVatIncluded: paymentRequest?.is_vat_included,
    vatAmount: paymentRequest?.vat_amount,
    requestCurrency: paymentRequest?.request_currency,
    claim,
    conversionRate: paymentRequest?.conversion_rate,
  });

  const issuesAndSeveritiesForPaymentsAboveReserves = getPaymentsAboveReserveIssuesAndSeverities({
    isSubReservesConfigEnabled,
    isMultipleSubReservePaymentsEnabled,
    payableWithReserve,
    paymentAmount: reserveAdjustmentAmount,
    amountWithoutDeductible: amount,
    subReserveAmounts: values['sub_reserve_amounts'],
    subReserveKeyForAdjustReserveDeductible: values['sub_reserve_key_for_adjust_reserve_deductible'],
    shouldDisablePaymentExceedingReserve,
    subReservesConfigMap,
    currencyFormatter,
    currentPaymentRequestAmount,
    isApprovalDialogOtherwiseMakePaymentRequest: true,
    vatAmountToNetOut,
    shouldReduceReservesByNetPaid: reduceReservesByNetPaid,
  });

  const newPaidSum = toCents(payableWithReserve.paid_sum + amount);
  const checkIsExceedsUserAuthority = () => isLimitExists(userExposureLimit) && userExposureLimit < amount;
  const disablePayment = shouldDisablePayment({
    shouldDisablePaymentExceedingReserve,
    issuesAndSeveritiesForPaymentsAboveReserves,
    isSubmitting,
    checkIsExceedsClosedExposurePaymentLimit,
    isLoadingPaymentIssues,
    isPaymentIssuePreventingPayment,
  });

  function switchAmountField() {
    switch (values['decision']) {
      case 'approve':
        return (
          <ExposureMonetaryValueTextFieldFormik
            key="approve"
            id="request_amount"
            label="Payment"
            showOnly
            exposure={exposure}
            claim={claim}
          />
        );
      case 'approve_different':
        return (
          <ExposureMonetaryValueTextFieldFormik
            key="approve_different"
            id="decision_amount"
            label="Payment"
            InputLabelProps={{ shrink: true }}
            exposure={exposure}
            claim={claim}
          />
        );
      case 'decline':
        return <MonetaryValueTextFieldFormik key="decline" id="request_amount" label="Payment" value={0} showOnly />;
      default:
        return (
          <div style={{ visibility: 'hidden' }}>
            <MonetaryValueTextFieldFormik
              key="placeholder"
              id="request_amount"
              label="New reserve"
              value={0}
              showOnly
            />
          </div>
        );
    }
  }

  function switchSubmitButtonName() {
    if (!values['decision']) {
      return 'Approve Request';
    }

    switch (values['decision']) {
      case 'approve':
        return checkIsExceedsUserAuthority() ? 'Request Supervisor Approval' : 'Approve Request';
      case 'approve_different':
        return checkIsExceedsUserAuthority() ? 'Request Supervisor Approval' : 'Approve Different Request';
      case 'decline':
        return 'Decline Request';
      default:
        throw Error(`Unknown value for decision: ${values['decision']}`);
    }
  }

  const requestActionsOptions = [
    {
      key: 'approve',
      value: 'approve',
      label: (
        <Text weight={Text.WEIGHTS.REGULAR} variant={Text.VARIANTS.SM}>
          Approve
        </Text>
      ),
    },

    {
      key: 'decline',
      value: 'decline',
      label: (
        <Text weight={Text.WEIGHTS.REGULAR} variant={Text.VARIANTS.SM}>
          Decline
        </Text>
      ),
    },
  ];

  if (!(paymentRequest?.request_currency !== claim?.policy?.policy_currency || isSubReservesConfigEnabled)) {
    requestActionsOptions.splice(1, 0, {
      key: 'approve_different',
      value: 'approve_different',
      label: (
        <Text weight={Text.WEIGHTS.REGULAR} variant={Text.VARIANTS.SM}>
          Approve different sum
        </Text>
      ),
    });
  }

  return (
    <CardDialog
      title={`${capitalize(payableType)} Payment Approval`}
      {...cardDialogProps}
      onClose={() => {
        handleReset();
        onCancel();
      }}
    >
      <LoadingSwitch isLoading={isUserExposureLimitLoading}>
        {AdditionalDialogHeader}
        <AnomalousPaymentBanner
          {...getAnomalousPaymentBannerProps({
            checkIsExceedsUserAuthority,
            checkIsExceedsClosedExposurePaymentLimit,
            issuesAndSeveritiesForPaymentsAboveReserves,
            policyLimitBreaches,
            isPaymentIssuePreventingPayment,
          })}
        />
        <PaymentRequestDisplay
          paymentRequest={paymentRequest}
          exposure={exposure}
          claim={claim}
          payableType={payableType}
          newPaidSum={newPaidSum}
          payableWithReserve={payableWithReserve}
        />
        {Object.keys(paymentRequest.method_specific_approval_requests || {}).map((methodSpecificApprovalRequestKey) => (
          <div className={styles?.alertBannerContainer} key={methodSpecificApprovalRequestKey}>
            <AlertBanner
              alertType={AlertBanner.ALERT_TYPES.WARNING}
              title={paymentRequest.method_specific_approval_requests[methodSpecificApprovalRequestKey].message}
              withIcon
            />
          </div>
        ))}
        <ApprovalRequests approvalRequests={paymentRequest?.approval_requests} className="mb-16" />
        <ToggleButtonGroupFormik id="decision" options={requestActionsOptions} />
        <CurrencyFormatterContextProvider currency={paymentRequest?.request_currency}>
          {switchAmountField()}
        </CurrencyFormatterContextProvider>
        <div className={classes.inputContainer}>
          <TextFieldFormik id="note" label="Note" rows={2} className={classes.textField} fullWidth multiline />
        </div>
        <div className={classes.buttonsContainer}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={disablePayment || !values['decision']}
          >
            {switchSubmitButtonName()}
          </Button>
        </div>
      </LoadingSwitch>
    </CardDialog>
  );
}

PaymentRequestApprovalDialogFormikInner.propTypes = {
  cardDialogProps: PropTypes.object.isRequired,
  paymentRequest: PropTypes.object.isRequired,
  payableWithReserve: PropTypes.object.isRequired,
  payableType: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  claim: PropTypes.object.isRequired,
  exposure: PropTypes.object.isRequired,
  AdditionalDialogHeader: PropTypes.node,
};

export default PaymentRequestApprovalDialog;
